import { THEMATIC_ATTRIBUTES } from "../model/inp/thematicAttributes.ts";
import { formatUnit } from "../model/inp/properties.ts";
import { viridisColor } from "./thematicColors.ts";
import { useLanguage, useT } from "../i18n/hooks.ts";
import { formatNumber } from "../i18n/formatNumber.ts";

// Samples all 5 anchor stops (see `viridisColor`) so the CSS preview matches
// the per-element ramp exactly rather than approximating it with just the
// endpoints, which would silently drop the two intermediate anchors.
const GRADIENT = `linear-gradient(to right, ${viridisColor(0)}, ${viridisColor(0.25)}, ${viridisColor(0.5)}, ${viridisColor(0.75)}, ${viridisColor(1)})`;

// 5 tick positions in [0, 1] — the two endpoints (min/max) plus 3 evenly
// spaced intermediate ticks. Reading a value off a continuous gradient with
// only min/max labels is guesswork (audit finding); the intermediate ticks
// let the eye estimate a color's position to a real number.
const TICK_FRACTIONS = [0, 0.25, 0.5, 0.75, 1];

export function ThematicMapBar({
  attributeKey,
  onAttributeChange,
  min,
  max,
  onClose,
  hasResults,
  flowUnits,
}: {
  attributeKey: string | null;
  onAttributeChange: (key: string | null) => void;
  min: number;
  max: number;
  onClose: () => void;
  /** A `requiresResults` attribute only appears once a simulation has completed — before that it would show a flat, meaningless "everything is 0" map. */
  hasResults: boolean;
  flowUnits: string;
}) {
  const t = useT();
  const language = useLanguage();
  const available = THEMATIC_ATTRIBUTES.filter((a) => hasResults || !a.requiresResults);
  const unitFor = (a: (typeof THEMATIC_ATTRIBUTES)[number]) => formatUnit(a.unitKind, flowUnits);
  const optionLabel = (a: (typeof THEMATIC_ATTRIBUTES)[number]) => {
    const unit = unitFor(a);
    return unit ? `${t(a.labelKey)} (${unit})` : t(a.labelKey);
  };
  const byCategory = {
    node: available.filter((a) => a.category === "node"),
    link: available.filter((a) => a.category === "link"),
    subcatchment: available.filter((a) => a.category === "subcatchment"),
  };
  const selectedAttribute = available.find((a) => a.key === attributeKey) ?? null;
  const hasSelection = attributeKey !== null;
  const selectedUnit = selectedAttribute ? unitFor(selectedAttribute) : "";
  const span = max - min;

  return (
    <div className="results-animation-bar thematic-map-bar">
      <select
        value={attributeKey ?? ""}
        onChange={(e) => onAttributeChange(e.target.value || null)}
        aria-label={t("thematicMap.attributePicker")}
      >
        <option value="">{t("thematicMap.pickAttribute")}</option>
        <optgroup label={t("thematicMap.categoryNode")}>
          {byCategory.node.map((a) => (
            <option key={a.key} value={a.key}>
              {optionLabel(a)}
            </option>
          ))}
        </optgroup>
        <optgroup label={t("thematicMap.categoryLink")}>
          {byCategory.link.map((a) => (
            <option key={a.key} value={a.key}>
              {optionLabel(a)}
            </option>
          ))}
        </optgroup>
        <optgroup label={t("thematicMap.categorySubcatchment")}>
          {byCategory.subcatchment.map((a) => (
            <option key={a.key} value={a.key}>
              {optionLabel(a)}
            </option>
          ))}
        </optgroup>
      </select>
      {hasSelection && (
        <div className="thematic-map-legend">
          <div className="thematic-map-gradient" style={{ background: GRADIENT }} />
          <div className="thematic-map-ticks">
            {TICK_FRACTIONS.map((frac, i) => {
              const value = min + span * frac;
              const isLast = i === TICK_FRACTIONS.length - 1;
              return (
                <span key={frac} className="thematic-map-tick" style={{ left: `${frac * 100}%` }}>
                  {formatNumber(value, language, 2)}
                  {selectedUnit && isLast && ` ${selectedUnit}`}
                </span>
              );
            })}
          </div>
        </div>
      )}
      <button className="button" onClick={onClose}>
        {t("common.close")}
      </button>
    </div>
  );
}
